import { useEffect } from 'react';

/**
 * The browser tab's title, the page's meta description and their Open Graph
 * twins, for one storefront screen.
 *
 * Pages and posts carry SEO fields the merchant filled in, and a field that is
 * stored but never rendered is decoration. This puts them where they are read.
 * The API already answers in the shopper's language (docs/i18n.md §7), so the
 * words arriving here are the localised ones; the language links beside them
 * are `StorefrontHead`'s.
 *
 * Everything is restored on the way out, for the same reason the favicon is:
 * the document head belongs to whatever loaded first - in production the
 * store's own index.html, in preview the dashboard's - and a merchant who
 * closes a preview should not find their dashboard tab still named after a
 * blog post.
 */
export interface StorefrontMeta {
  title?: string | null;
  description?: string | null;
  /** Appended after the title, so a tab reads "Our story · Alpha Threads". */
  storeName?: string | null;
}

/** Sets the description tag if the document has one, returning what it said. */
function setMetaDescription(value: string): string | null {
  const tag = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!tag) return null;

  const previous = tag.content;
  tag.content = value;
  return previous;
}

/**
 * Sets an Open Graph property, creating the tag when the document has none,
 * and returns how to put the head back.
 */
function setOpenGraph(property: string, value: string): () => void {
  const existing = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (existing) {
    const previous = existing.content;
    existing.content = value;
    return () => {
      existing.content = previous;
    };
  }
  const tag = document.createElement('meta');
  tag.setAttribute('property', property);
  tag.content = value;
  document.head.appendChild(tag);
  return () => tag.remove();
}

export function useStorefrontMeta({ title, description, storeName }: StorefrontMeta) {
  useEffect(() => {
    if (!title) return;

    const previousTitle = document.title;
    const fullTitle = storeName ? `${title} · ${storeName}` : title;
    document.title = fullTitle;

    const previousDescription = description ? setMetaDescription(description) : null;
    const restoreOpenGraph = [setOpenGraph('og:title', fullTitle)];
    if (description) restoreOpenGraph.push(setOpenGraph('og:description', description));

    return () => {
      document.title = previousTitle;
      if (previousDescription !== null) setMetaDescription(previousDescription);
      for (const restore of restoreOpenGraph.reverse()) restore();
    };
  }, [title, description, storeName]);
}
